import { useEffect, useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  X,
  Home,
  Database,
  Dna,
  FlaskConical,
  Layers,
  History,
  ArrowRight,
  Command,
  Clock,
  Zap,
  Atom,
  TestTube,
  Beaker,
} from 'lucide-react'
import { useCommandPaletteStore } from '../../stores/commandPaletteStore'
import { useSearchHistoryStore, formatTimeAgo } from '../../stores/searchHistoryStore'
import { useKeyboardShortcut, useEscapeKey } from '../../hooks/useKeyboardShortcut'
import { useDebounce } from '../../hooks/useDebounce'
import { useGlobalSearch, useCompoundSearch } from '../../api/hooks'

/**
 * Detect the type of search query based on patterns
 */
type QueryType = 'blast' | 'substructure' | 'reaction' | 'cluster' | 'general'

function detectQueryType(query: string): QueryType {
  const trimmed = query.trim()

  // FASTA sequence detection - starts with > or contains typical amino acid patterns
  if (trimmed.startsWith('>') || /^[ACDEFGHIKLMNPQRSTVWY\s]{20,}$/i.test(trimmed)) {
    return 'blast'
  }

  // SMARTS/SMILES pattern detection - contains chemical notation
  // Look for: brackets with atom specs, aromatic notation, ring numbers
  if (/[\[\]#@]/.test(trimmed) || /[cnosp][0-9]/.test(trimmed) || /[A-Z][a-z]?\(/.test(trimmed)) {
    return 'substructure'
  }

  // MIBiG accession pattern
  if (/^BGC\d{7}$/i.test(trimmed)) {
    return 'cluster'
  }

  // Compound/reaction search patterns - enzyme names, cofactors, metabolites
  const reactionKeywords = [
    'atp', 'adp', 'nad', 'nadh', 'nadp', 'fad', 'coenzyme', 'cofactor',
    '-ase', 'kinase', 'transferase', 'synthase', 'reductase', 'oxidase',
    'dehydrogenase', 'phosphate', 'adenosyl', 'methyl', 'acetyl', 'hydroxyl',
    'chebi:', 'rhea:', 'glucose', 'pyruvate', 'acetate', 'succinate'
  ]
  const lowerQuery = trimmed.toLowerCase()
  if (reactionKeywords.some(kw => lowerQuery.includes(kw))) {
    return 'reaction'
  }

  return 'general'
}

function getQueryTypeInfo(type: QueryType): { label: string; icon: React.ReactNode; color: string; description: string } {
  switch (type) {
    case 'blast':
      return {
        label: 'BLAST Search',
        icon: <Zap className="w-4 h-4" />,
        color: 'text-accent-purple',
        description: 'Sequence detected - run BLAST search'
      }
    case 'substructure':
      return {
        label: 'Substructure Search',
        icon: <Atom className="w-4 h-4" />,
        color: 'text-accent-amber',
        description: 'SMARTS pattern detected - search chemical structures'
      }
    case 'reaction':
      return {
        label: 'Reaction Search',
        icon: <TestTube className="w-4 h-4" />,
        color: 'text-accent-green',
        description: 'Compound/enzyme detected - search reactions'
      }
    case 'cluster':
      return {
        label: 'Cluster Search',
        icon: <Database className="w-4 h-4" />,
        color: 'text-accent-cyan',
        description: 'MIBiG accession detected'
      }
    default:
      return {
        label: 'Global Search',
        icon: <Search className="w-4 h-4" />,
        color: 'text-gray-400',
        description: 'Search clusters and proteins'
      }
  }
}

interface QuickNavItem {
  id: string
  label: string
  path: string
  icon: React.ReactNode
  description: string
}

const quickNavItems: QuickNavItem[] = [
  { id: 'home', label: 'Home', path: '/', icon: <Home className="w-4 h-4" />, description: 'Go to homepage' },
  { id: 'clusters', label: 'Cluster Browser', path: '/clusters', icon: <Database className="w-4 h-4" />, description: 'Browse all gene clusters' },
  { id: 'blast', label: 'BLAST Search', path: '/blast', icon: <Dna className="w-4 h-4" />, description: 'Sequence similarity search' },
  { id: 'reactions', label: 'Reaction Search', path: '/reactions', icon: <FlaskConical className="w-4 h-4" />, description: 'Search biochemical reactions' },
  { id: 'substructure', label: 'Substructure Search', path: '/substructure', icon: <Layers className="w-4 h-4" />, description: 'Chemical substructure search' },
]

export function CommandPalette() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const { isOpen, query, recentSearches, open, close, setQuery, addRecentSearch } = useCommandPaletteStore()
  const { history, addToHistory } = useSearchHistoryStore()

  // Keyboard shortcuts
  useKeyboardShortcut('k', open, { ctrl: true })
  useEscapeKey(close)

  // Debounced query for API search
  const debouncedQuery = useDebounce(query, 300)

  // Detect query type for smart search routing
  const queryType = useMemo(() => detectQueryType(query), [query])
  const queryTypeInfo = useMemo(() => getQueryTypeInfo(queryType), [queryType])

  // API search - only search when query has at least 2 characters
  const { data: searchResults, isLoading: isLoadingGlobal } = useGlobalSearch(
    queryType === 'general' && debouncedQuery.length >= 2 ? debouncedQuery : ''
  )

  // Compound/Reaction search
  const { data: compoundResults, isLoading: isLoadingCompounds } = useCompoundSearch(
    queryType === 'reaction' && debouncedQuery.length >= 2 ? debouncedQuery : ''
  )

  const isLoading = isLoadingGlobal || isLoadingCompounds

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Build results list
  const allResults = useMemo(() => {
    const results: Array<{
      type: 'nav' | 'cluster' | 'protein' | 'recent' | 'history' | 'action' | 'compound' | 'globalCompound'
      id: string
      label: string
      sublabel?: string
      icon: React.ReactNode
      action: () => void
    }> = []

    // If no query, show quick nav, recently visited, and recent searches
    if (!query.trim()) {
      // Quick navigation
      quickNavItems.forEach((item) => {
        results.push({
          type: 'nav',
          id: `nav-${item.id}`,
          label: item.label,
          sublabel: item.description,
          icon: item.icon,
          action: () => {
            navigate(item.path)
            close()
          },
        })
      })

      // Recently visited items (from history)
      history.slice(0, 5).forEach((item) => {
        const icon = item.type === 'cluster'
          ? <Database className="w-4 h-4 text-accent-cyan" />
          : item.type === 'protein'
            ? <Dna className="w-4 h-4 text-accent-purple" />
            : <Clock className="w-4 h-4 text-gray-400" />

        results.push({
          type: 'history',
          id: `history-${item.id}`,
          label: item.label,
          sublabel: `${item.sublabel || ''} • ${formatTimeAgo(item.timestamp)}`,
          icon,
          action: () => {
            navigate(item.path)
            close()
          },
        })
      })

      // Recent searches
      recentSearches.forEach((search) => {
        results.push({
          type: 'recent',
          id: `recent-${search}`,
          label: search,
          sublabel: 'Recent search',
          icon: <History className="w-4 h-4" />,
          action: () => {
            setQuery(search)
          },
        })
      })
    } else {
      // Smart search action based on detected query type
      if (queryType !== 'general' && query.trim().length >= 2) {
        // Add primary action for detected query type
        results.push({
          type: 'action',
          id: `action-${queryType}`,
          label: `${queryTypeInfo.label}: "${query.slice(0, 50)}${query.length > 50 ? '...' : ''}"`,
          sublabel: queryTypeInfo.description,
          icon: <span className={queryTypeInfo.color}>{queryTypeInfo.icon}</span>,
          action: () => {
            addRecentSearch(query)
            switch (queryType) {
              case 'blast':
                // Navigate to BLAST with sequence pre-filled
                navigate('/blast', { state: { sequence: query } })
                break
              case 'substructure':
                // Navigate to substructure with SMARTS pre-filled
                navigate('/substructure', { state: { smarts: query } })
                break
              case 'reaction':
                // Navigate to reaction search with query
                navigate(`/reactions?search=${encodeURIComponent(query)}`)
                break
              case 'cluster':
                // Direct navigation to cluster
                navigate(`/clusters/${query.toUpperCase()}`)
                break
            }
            close()
          },
        })
      }

      // Filter quick nav by query (only for general searches or short queries)
      if (queryType === 'general' || query.trim().length < 2) {
        quickNavItems
          .filter((item) =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.description.toLowerCase().includes(query.toLowerCase())
          )
          .forEach((item) => {
            results.push({
              type: 'nav',
              id: `nav-${item.id}`,
              label: item.label,
              sublabel: item.description,
              icon: item.icon,
              action: () => {
                navigate(item.path)
                close()
              },
            })
          })
      }

      // Add compound results for reaction search
      if (queryType === 'reaction' && compoundResults?.data) {
        compoundResults.data.slice(0, 8).forEach((compound) => {
          results.push({
            type: 'compound',
            id: `compound-${compound.chebi_accession}`,
            label: compound.name || compound.chebi_accession,
            sublabel: `${compound.chebi_accession}${compound.formula ? ` • ${compound.formula}` : ''}`,
            icon: <FlaskConical className="w-4 h-4 text-accent-green" />,
            action: () => {
              addRecentSearch(query)
              addToHistory({
                id: compound.chebi_accession,
                type: 'compound',
                label: compound.name || compound.chebi_accession,
                sublabel: compound.formula,
                path: `/reactions?compound=${compound.chebi_accession}`,
              })
              navigate(`/reactions?compound=${compound.chebi_accession}`)
              close()
            },
          })
        })
      }

      // Add general search results (clusters and proteins)
      if (queryType === 'general' && searchResults) {
        // Clusters
        searchResults.clusters?.slice(0, 5).forEach((cluster) => {
          const sublabel = `${cluster.organism_name} - ${cluster.biosyn_class?.join(', ') || 'Unknown'}`
          results.push({
            type: 'cluster',
            id: `cluster-${cluster.mibig_accession}`,
            label: cluster.mibig_accession,
            sublabel,
            icon: <Database className="w-4 h-4 text-accent-cyan" />,
            action: () => {
              addRecentSearch(query)
              addToHistory({
                id: cluster.mibig_accession,
                type: 'cluster',
                label: cluster.mibig_accession,
                sublabel: cluster.organism_name,
                path: `/clusters/${cluster.mibig_accession}`,
              })
              navigate(`/clusters/${cluster.mibig_accession}`)
              close()
            },
          })
        })

        // Proteins
        searchResults.proteins?.slice(0, 5).forEach((protein) => {
          const sublabel = `${protein.product || 'No description'} - ${protein.mibig_accession}`
          results.push({
            type: 'protein',
            id: `protein-${protein.mibig_accession}-${protein.protein_id}`,
            label: protein.protein_id,
            sublabel,
            icon: <Dna className="w-4 h-4 text-accent-purple" />,
            action: () => {
              addRecentSearch(query)
              addToHistory({
                id: `${protein.mibig_accession}-${protein.protein_id}`,
                type: 'protein',
                label: protein.protein_id,
                sublabel: protein.product || protein.mibig_accession,
                path: `/proteins/${protein.mibig_accession}/${protein.protein_id}`,
              })
              navigate(`/proteins/${protein.mibig_accession}/${protein.protein_id}`)
              close()
            },
          })
        })

        // Compounds (from unified search)
        searchResults.compounds?.slice(0, 5).forEach((compound) => {
          const sublabel = `${compound.mibig_accession}${compound.database_id ? ` • ${compound.database_id}` : ''}`
          results.push({
            type: 'globalCompound',
            id: `globalCompound-${compound.mibig_accession}-${compound.compound}`,
            label: compound.compound,
            sublabel,
            icon: <Beaker className="w-4 h-4 text-accent-amber" />,
            action: () => {
              addRecentSearch(query)
              addToHistory({
                id: `${compound.mibig_accession}-${compound.compound}`,
                type: 'compound',
                label: compound.compound,
                sublabel: compound.mibig_accession,
                path: `/clusters/${compound.mibig_accession}`,
              })
              navigate(`/clusters/${compound.mibig_accession}`)
              close()
            },
          })
        })
      }

      // Always offer alternative search modes when query is long enough
      if (query.trim().length >= 2 && queryType === 'general') {
        // Offer to search as compound/reaction
        results.push({
          type: 'action',
          id: 'action-search-reaction',
          label: `Search reactions for "${query.slice(0, 30)}${query.length > 30 ? '...' : ''}"`,
          sublabel: 'Search compounds and biochemical reactions',
          icon: <TestTube className="w-4 h-4 text-accent-green" />,
          action: () => {
            addRecentSearch(query)
            navigate(`/reactions?search=${encodeURIComponent(query)}`)
            close()
          },
        })
      }
    }

    return results
  }, [query, queryType, queryTypeInfo, searchResults, compoundResults, recentSearches, history, navigate, close, addRecentSearch, setQuery, addToHistory])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (allResults[selectedIndex]) {
            allResults[selectedIndex].action()
          }
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, selectedIndex, allResults])

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex])

  if (!isOpen) return null

  const paletteContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
        onClick={close}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Palette */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl bg-dark-bg-secondary border border-white/10 rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clusters, proteins, sequences, compounds..."
              className="flex-1 bg-transparent text-white placeholder-gray-500 focus:outline-none text-lg"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-white/10 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {/* Query type indicator */}
            {query.trim().length >= 2 && queryType !== 'general' && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${queryTypeInfo.color} bg-white/5`}>
                {queryTypeInfo.icon}
                <span>{queryTypeInfo.label}</span>
              </div>
            )}
            <div className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded text-xs text-gray-400">
              <Command className="w-3 h-3" />K
            </div>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[400px] overflow-y-auto">
            {isLoading && debouncedQuery.length >= 2 ? (
              <div className="px-4 py-8 text-center text-gray-400">
                <div className="animate-spin w-6 h-6 border-2 border-accent-cyan border-t-transparent rounded-full mx-auto mb-2" />
                Searching...
              </div>
            ) : allResults.length > 0 ? (
              <div className="py-2">
                {/* Group by type */}
                {!query.trim() && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quick Navigation
                    </div>
                    {allResults
                      .filter((r) => r.type === 'nav')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}
                    {history.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                          Recently Visited
                        </div>
                        {allResults
                          .filter((r) => r.type === 'history')
                          .map((result) => {
                            const actualIndex = allResults.findIndex((r) => r.id === result.id)
                            return (
                              <ResultItem
                                key={result.id}
                                result={result}
                                isSelected={selectedIndex === actualIndex}
                                onClick={result.action}
                                onMouseEnter={() => setSelectedIndex(actualIndex)}
                              />
                            )
                          })}
                      </>
                    )}
                    {recentSearches.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                          Recent Searches
                        </div>
                        {allResults
                          .filter((r) => r.type === 'recent')
                          .map((result) => {
                            const actualIndex = allResults.findIndex((r) => r.id === result.id)
                            return (
                              <ResultItem
                                key={result.id}
                                result={result}
                                isSelected={selectedIndex === actualIndex}
                                onClick={result.action}
                                onMouseEnter={() => setSelectedIndex(actualIndex)}
                              />
                            )
                          })}
                      </>
                    )}
                  </>
                )}

                {query.trim() && (
                  <>
                    {/* Smart search actions */}
                    {allResults.filter((r) => r.type === 'action').length > 0 && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </div>
                    )}
                    {allResults
                      .filter((r) => r.type === 'action')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}

                    {/* Compound results */}
                    {allResults.filter((r) => r.type === 'compound').length > 0 && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                        Compounds
                      </div>
                    )}
                    {allResults
                      .filter((r) => r.type === 'compound')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}

                    {/* Page navigation */}
                    {allResults.filter((r) => r.type === 'nav').length > 0 && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                        Pages
                      </div>
                    )}
                    {allResults
                      .filter((r) => r.type === 'nav')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}

                    {/* Cluster results */}
                    {allResults.filter((r) => r.type === 'cluster').length > 0 && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                        Clusters
                      </div>
                    )}
                    {allResults
                      .filter((r) => r.type === 'cluster')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}

                    {/* Protein results */}
                    {allResults.filter((r) => r.type === 'protein').length > 0 && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                        Proteins
                      </div>
                    )}
                    {allResults
                      .filter((r) => r.type === 'protein')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}

                    {/* Global compound results (from unified search) */}
                    {allResults.filter((r) => r.type === 'globalCompound').length > 0 && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider mt-2">
                        Compounds
                      </div>
                    )}
                    {allResults
                      .filter((r) => r.type === 'globalCompound')
                      .map((result) => {
                        const actualIndex = allResults.findIndex((r) => r.id === result.id)
                        return (
                          <ResultItem
                            key={result.id}
                            result={result}
                            isSelected={selectedIndex === actualIndex}
                            onClick={result.action}
                            onMouseEnter={() => setSelectedIndex(actualIndex)}
                          />
                        )
                      })}
                  </>
                )}
              </div>
            ) : query.trim() && debouncedQuery.length >= 2 ? (
              <div className="px-4 py-8 text-center text-gray-400">
                No results found for "{query}"
              </div>
            ) : query.trim() && debouncedQuery.length < 2 ? (
              <div className="px-4 py-8 text-center text-gray-400">
                Type at least 2 characters to search
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">Esc</kbd>
                Close
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )

  return typeof document !== 'undefined'
    ? createPortal(paletteContent, document.body)
    : null
}

interface ResultItemProps {
  result: {
    id: string
    label: string
    sublabel?: string
    icon: React.ReactNode
  }
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
}

function ResultItem({ result, isSelected, onClick, onMouseEnter }: ResultItemProps) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`
        w-full flex items-center gap-3 px-3 py-2 text-left transition-colors
        ${isSelected ? 'bg-accent-cyan/10 text-white' : 'text-gray-300 hover:bg-white/5'}
      `}
    >
      <span className={isSelected ? 'text-accent-cyan' : 'text-gray-400'}>
        {result.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{result.label}</div>
        {result.sublabel && (
          <div className="text-xs text-gray-500 truncate">{result.sublabel}</div>
        )}
      </div>
      {isSelected && <ArrowRight className="w-4 h-4 text-accent-cyan flex-shrink-0" />}
    </button>
  )
}

export default CommandPalette
